import { EditorCardProvider } from '@atlaskit/editor-card-provider/provider';
import type { CardAppearance, EnvironmentsKeys } from '@atlaskit/linking-common/types';
import type CardClient from '@atlaskit/link-provider/client';

import { expVal, isExperimentEnabled } from '@confluence/feature-experiments';

const isTrelloBoardUrl = (url: string) => !!url.match(/^https:\/\/(.*\.)?trello\.com\/b/);

const isFigmaFileUrl = (url: string) =>
	!!url.match(/^https:\/\/(.*\.)?figma\.com\/(design|board|file|proto|slides)/);

const isFigmaMakeSiteUrl = (url: string) => !!url.match(/^https:\/\/.+\.figma\.site/);

const isMiroUrl = (url: string) => !!url.match(/^https:\/\/(.*\.)?miro\.com\/app\/board/);

const isCanvaUrl = (url: string) => !!url.match(/^https:\/\/(.*\.)?canva\.com\/design/);

const isLucidUrl = (url: string) => !!url.match(/^https:\/\/(.*\.)?lucid\.app\/lucidchart/);

const isGoogleSlidesUrl = (url: string) =>
	!!url.match(/^https:\/\/docs\.google\.com\/presentation/);

const isOneDrivePowerPointUrl = (url: string) =>
	!!url.match(/^https:\/\/onedrive\.live\.com\/:p/) || !!url.match(/^https:\/\/1drv\.ms\/p/);

const isTrelloCardUrl = (url: string) => !!url.match(/^https:\/\/(.*\.)?trello\.com\/c/);

const isPowerBIUrl = (url: string) =>
	!!url.match(/^https:\/\/app\.powerbi\.com\/(groups|reportEmbed)/);

const isGoogleDocsUrl = (url: string) => !!url.match(/^https:\/\/docs\.google\.com\/document/);

// https://learn.microsoft.com/en-us/onedrive/developer/controls/file-pickers/?view=odsp-graph-online
const isLearnMicrosoftUrl = (url: string) => !!url.match(/^https:\/\/learn\.microsoft\.com/);

// https://support.google.com/mail/answer/112508?hl=en&co=GENIE.Platform%3DDesktop
const isSupportGoogleUrl = (url: string) => !!url.match(/^https:\/\/support\.google\.com/);

// https://docs.github.com/en/codespaces
const isDocsGithubUrl = (url: string) => !!url.match(/^https:\/\/docs\.github\.com/);

const isDropboxUrl = (url: string) =>
	!!url.match(/^https:\/\/(.*\.)?dropbox\.com\/(home|preview|s|sh|sc|t|scl)/);

const isBoxUrl = (url: string) =>
	!!url.match(/^https:\/\/(.*\.)?box\.com\/(folder|file|notes|integrations|services|s|v)/);

const isSlackUrl = (url: string) => !!url.match(/^https:\/\/(.*\.)?slack\.com\/(archives|client)/);

const isTeamsUrl = (url: string) =>
	!!url.match(/^https:\/\/teams\.microsoft\.com\/l\/channel/) ||
	!!url.match(/^https:\/\/teams\.cloud\.microsoft\/l\/channel/);

const isLovableAppUrl = (url: string) => !!url.match(/^https:\/\/.+\.lovable\.app/);

const isReplitAppUrl = (url: string) => !!url.match(/^https:\/\/.+\.replit\.app/);
export type SupportedUrlFilter = (url: string) => boolean;

/**
 * Confluence-specific EditorCardProvider that extends the platform EditorCardProvider.
 * This class can be extended further by Confluence to customize smart link behavior.
 */
export class ConfluenceEditorCardProvider extends EditorCardProvider {
	override readonly name: 'editorCardProvider' | string;

	constructor(
		envKey?: EnvironmentsKeys,
		baseUrlOverride?: string,
		onResolve?: (url: string) => void,
		customCardClient?: CardClient,
		supportedUrlFilter?: SupportedUrlFilter,
		name?: string,
	) {
		super(envKey, baseUrlOverride, 'CONFLUENCE', onResolve, customCardClient, supportedUrlFilter);
		this.name = name ?? 'editorCardProvider';
	}

	protected getHardCodedAppearance(url: string): CardAppearance | undefined {
		if (isExperimentEnabled('confluence_smartlink_default_display_types')) {
			if (this.shouldDisplayAsEmbed(url)) {
				return 'embed';
			}

			if (this.shouldDisplayAsCard(url)) {
				return 'block';
			}
		}

		return super.getHardCodedAppearance(url);
	}

	/**
	 * Opt in to honouring an explicitly-requested embed appearance as the default
	 * (validated, with a card fallback) for the quick-insert embed flow. Both active
	 * cohorts of the quick-insert embeds experiment (`prioritizeLinkInQIM` and
	 * `prioritizeEmbedInQIM`) enable this — they differ only in quick-insert menu
	 * ordering, not in embedding behaviour. Only `control` and unenrolled users keep
	 * the existing behaviour.
	 *
	 * The platform only calls this when a link is inserted with a requested embed
	 * appearance and the user has no saved preference, so the exposure here is
	 * scoped to the quick-insert embed insertion path.
	 */
	protected shouldHonorRequestedEmbedAsDefault(): boolean {
		return expVal('confluence_quick_insert_embeds', 'cohort', 'control') !== 'control';
	}

	private shouldDisplayAsEmbed(url: string): boolean {
		return (
			isTrelloBoardUrl(url) ||
			isFigmaFileUrl(url) ||
			isFigmaMakeSiteUrl(url) ||
			isMiroUrl(url) ||
			isCanvaUrl(url) ||
			isLucidUrl(url) ||
			isGoogleSlidesUrl(url) ||
			isOneDrivePowerPointUrl(url)
		);
	}

	private shouldDisplayAsCard(url: string): boolean {
		return (
			isTrelloCardUrl(url) ||
			isPowerBIUrl(url) ||
			isGoogleDocsUrl(url) ||
			isLearnMicrosoftUrl(url) ||
			isSupportGoogleUrl(url) ||
			isDocsGithubUrl(url) ||
			isDropboxUrl(url) ||
			isBoxUrl(url) ||
			isSlackUrl(url) ||
			isTeamsUrl(url) ||
			isLovableAppUrl(url) ||
			isReplitAppUrl(url)
		);
	}
}
